import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import SEO from '../components/common/SEO';
import './FAQPage.css';

function FAQPage() {
  const [openItems, setOpenItems] = useState(new Set([0]));
  const contentRefs = React.useRef({});

  const toggleItem = (index) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(index)) {
      newOpenItems.delete(index);
    } else {
      newOpenItems.add(index);
    }
    setOpenItems(newOpenItems);
  };

  const getContentHeight = (index) => {
    const content = contentRefs.current[index];
    if (!content) return 0;
    return openItems.has(index) ? content.scrollHeight : 0;
  };

  const faqData = [
    {
      category: 'Getting Started',
      questions: [
        {
          question: 'What is tiniego?',
          answer:
            'tiniego turns your favourite photos into adorable little 3D printed figurines. Snap a pic, upload it, and watch our AI work its magic to create a unique mini-you (or mini-anyone!) that you can show off, collect, or gift to someone special.',
        },
        {
          question: 'How does it work?',
          answer:
            'Just upload a photo through our studio and let the magic happen! Our AI sketches your design, builds a 3D model, and boom—you\'ve got yourself a tiniego. Add it to your cart, and we\'ll 3D print it and pop it in the post.',
        },
        {
          question: 'Do I need an account to use tiniego?',
          answer:
            'Yep! You\'ll need to create a free account to make your tiniegos. This lets you save all your creations, keep track of orders, and watch your tiniegos journey to your doorstep.',
        },
      ],
    },
    {
      category: 'Creating Your tiniego',
      questions: [
        {
          question: 'What kind of photos work best?',
          answer:
            'Clear, well-lit photos work like a charm! Think good lighting, your subject front and centre, and not too much background chaos. Portrait-style shots usually give you the most detailed results—basically, the better the photo, the cooler your tiniego.',
        },
        {
          question: 'How long does it take to create my tiniego?',
          answer:
            'Usually just a few minutes! Your tiniego goes through three stages: sketching, assembling, and ta-da—completion. You can wander off and grab a cuppa while it happens. We\'ll save your progress so you can check back whenever.',
        },
        {
          question: 'Can I edit my tiniego after it\'s created?',
          answer:
            'Absolutely! Head to your Studio, click on any saved tiniego, and you can tweak the title, update the description, or even start fresh with a different photo. It\'s your creation—make it perfect!',
        },
        {
          question: 'What if I don\'t like the result?',
          answer:
            'No worries! You can create as many tiniegos as your heart desires. Only the ones you actually add to your cart and purchase get printed, so feel free to experiment until you land on something you love.',
        },
      ],
    },
    {
      category: 'Orders & Shipping',
      questions: [
        {
          question: 'How much does a tiniego cost?',
          answer:
            'Each tiniego is $25. Good news—creating and previewing is totally free! You only pay when you\'re ready to order the real deal.',
        },
        {
          question: 'How long does shipping take?',
          answer:
            'Once you place your order, we print your tiniego and ship it out within 5-7 business days. Delivery usually takes another 3-5 business days, depending on where you are. Not long at all!',
        },
        {
          question: 'Do I need to confirm my email before ordering?',
          answer:
            'Yep, you\'ll need to confirm your email before checking out. You can create tiniegos with an unconfirmed email, but we need that verified email before you can make a purchase.',
        },
        {
          question: 'Can I track my order?',
          answer:
            'Of course! Once your tiniego ships, we\'ll email you tracking info. You can also check your order status anytime in the Orders section of your account. No more wondering where your little friend is!',
        },
        {
          question: 'What if my tiniego arrives damaged?',
          answer:
            'Oh no! We pack everything super carefully, but if your tiniego arrives looking a bit worse for wear, just shoot our support team an email with some photos. We\'ll sort it out for you.',
        },
      ],
    },
    {
      category: 'Product Details',
      questions: [
        {
          question: 'What material are tiniegos made from?',
          answer:
            'We use high-quality white plastic (PLA) to 3D print your tiniego. It\'s durable, lightweight, and looks great on a shelf. Each one comes on its own little base, ready to show off.',
        },
        {
          question: 'What size are the figurines?',
          answer:
            'You get to choose! When you add your tiniego to the cart, you can pick a height between 20-80mm (that\'s roughly 0.8-3.1 inches). Perfect for desks, shelves, or anywhere you fancy—they\'re always tiniego-sized (hence the name!).',
        },
        {
          question: 'Can I paint my tiniego?',
          answer:
            'Go for it! Your tiniego arrives in crisp white, ready to display as-is or to get creative with. Lots of people love personalising theirs with acrylic paints—it\'s a fun little art project.',
        },
      ],
    },
    {
      category: 'Account & Privacy',
      questions: [
        {
          question: 'Can I delete my account?',
          answer:
            'Yep, just drop our support team a message and we\'ll sort it. Keep in mind that deleting your account will wipe out all your saved tiniegos and order history, so make sure you\'re ready to say goodbye!',
        },
        {
          question: 'How do I update my shipping address?',
          answer:
            'Head to the Profile section of your account, update your address, and hit save. Just make sure you do it before placing an order—otherwise your tiniego might end up on a little adventure to the wrong place!',
        },
      ],
    },
  ];

  return (
    <div className="faq-page">
      <SEO
        title="FAQ - Frequently Asked Questions"
        description="Find answers to common questions about tiniego - creating 3D printed figurines from your photos"
      />
      <Navbar />
      <div className="faq-container">
        <div className="faq-header">
          <h1>Frequently Asked Questions</h1>
          <p>Everything you need to know about creating your tiniego</p>
        </div>

        <div className="faq-content">
          {faqData.map((category, categoryIndex) => (
            <div key={categoryIndex} className="faq-category">
              <h2 className="faq-category-title">{category.category}</h2>
              <div className="faq-items">
                {category.questions.map((item, questionIndex) => {
                  const globalIndex = faqData
                    .slice(0, categoryIndex)
                    .reduce((acc, cat) => acc + cat.questions.length, 0) + questionIndex;
                  const isOpen = openItems.has(globalIndex);

                  return (
                    <div
                      key={questionIndex}
                      className={`faq-item ${isOpen ? 'open' : ''}`}
                    >
                      <button
                        className="faq-question"
                        onClick={() => toggleItem(globalIndex)}
                        aria-expanded={isOpen}
                      >
                        <span>{item.question}</span>
                        {isOpen ? (
                          <ChevronUp size={20} className="faq-icon" />
                        ) : (
                          <ChevronDown size={20} className="faq-icon" />
                        )}
                      </button>
                      <div
                        className="faq-answer"
                        style={{
                          height: getContentHeight(globalIndex),
                        }}
                      >
                        <div
                          ref={(el) => (contentRefs.current[globalIndex] = el)}
                          className="faq-answer-content"
                        >
                          <p>{item.answer}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="faq-contact">
          <h3>Still have questions?</h3>
          <p>
            Can't find what you're looking for? No worries—our support team is here to help!
          </p>
          <a href="mailto:support@tiniego.com" className="contact-button">
            Get in Touch
          </a>
        </div>
      </div>
    </div>
  );
}

export default FAQPage;
